import React, { useState, useEffect } from "react";
import { Video, Folder, ChevronLeft, Search, Loader, Play } from "lucide-react";
import { supabase } from "../lib/supabase";

interface VideoSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (videoHtml: string) => void;
}

interface VideoAsset {
  id: string;
  title: string;
  url: string;
  thumbnail_url?: string;
  duration?: number;
  size: number;
  folder?: string;
}

type VideoSize = "small" | "medium" | "large" | "full";

const VIDEO_SIZES: Record<VideoSize, { label: string; class: string }> = {
  small: { label: "Small (25%)", class: "w-1/4" },
  medium: { label: "Medium (50%)", class: "w-1/2" },
  large: { label: "Large (75%)", class: "w-3/4" },
  full: { label: "Full Width", class: "w-full" },
};

export default function VideoSelectorModal({
  isOpen,
  onClose,
  onSelect,
}: VideoSelectorModalProps) {
  const [videos, setVideos] = useState<VideoAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [folders, setFolders] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVideo, setSelectedVideo] = useState<VideoAsset | null>(null);
  const [selectedSize, setSelectedSize] = useState<VideoSize>("medium");

  useEffect(() => {
    if (isOpen) {
      fetchVideos();
      fetchFolders();
    } else {
      setSelectedVideo(null);
      setSelectedSize("medium");
      setSearchQuery("");
      setCurrentFolder(null);
    }
  }, [isOpen, currentFolder]);

  const fetchFolders = async () => {
    try {
      const { data, error } = await supabase.storage.from("videos").list();
      if (error) throw error;

      const folderList = data
        .filter((item) => !item.name.includes("."))
        .map((item) => item.name);

      setFolders(folderList);
    } catch (err) {
      console.error("Error fetching folders:", err);
    }
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("videos")
        .select("*")
        .eq("deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const filteredVideos =
        data?.filter((video) => {
          const matchesFolder = currentFolder
            ? video.folder === currentFolder
            : !video.folder;

          const matchesSearch = searchQuery
            ? video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (video.description &&
                video.description
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()))
            : true;

          return matchesFolder && matchesSearch;
        }) || [];

      setVideos(filteredVideos);
    } catch (err) {
      console.error("Error fetching videos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (e: React.MouseEvent, video: VideoAsset) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedVideo(video);
  };

  const handleInsert = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!selectedVideo) return;

    const sizeClass = VIDEO_SIZES[selectedSize].class;
    const videoHtml = `<div class="flex justify-center my-4">
  <video 
    src="${selectedVideo.url}" 
    controls
    class="${sizeClass} rounded-lg shadow-md"
    ${selectedVideo.thumbnail_url ? `poster="${selectedVideo.thumbnail_url}"` : ""}
  >
    Your browser does not support the video tag.
  </video>
</div>`;

    onSelect(videoHtml);
    onClose();
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedVideo(null);
    setSelectedSize("medium");
    setSearchQuery("");
    setCurrentFolder(null);
    onClose();
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
        onClick={handleModalClick}
      >
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Insert Video
            </h2>
            {currentFolder && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCurrentFolder(null);
                }}
                className="inline-flex items-center text-gray-600 hover:text-gray-900"
              >
                <ChevronLeft className="h-5 w-5 mr-1" />
                Back to root
              </button>
            )}
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search videos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
            </div>
          ) : !currentFolder ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {folders.map((folder) => (
                <button
                  key={folder}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentFolder(folder);
                  }}
                  className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  <Folder className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-900">{folder}</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {videos.map((video) => (
              <button
                key={video.id}
                onClick={(e) => handleSelect(e, video)}
                className={`group relative aspect-video rounded-lg overflow-hidden border ${
                  selectedVideo?.id === video.id
                    ? "border-[#F98B3D] ring-2 ring-[#F98B3D]"
                    : "border-gray-200 hover:border-[#F98B3D]"
                } focus:outline-none focus:ring-2 focus:ring-[#F98B3D]`}
              >
                {video.thumbnail_url ? (
                  <img
                    src={video.thumbnail_url}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <Video className="h-12 w-12 text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Play className="h-8 w-8 text-white" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                  <p className="text-sm truncate">{video.title}</p>
                </div>
              </button>
            ))}
          </div>

          {videos.length === 0 && (
            <div className="text-center py-12">
              <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No videos found</p>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Video className="h-5 w-5 text-gray-400" />
              <select
                value={selectedSize}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedSize(e.target.value as VideoSize);
                }}
                className="rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
                disabled={!selectedVideo}
              >
                {Object.entries(VIDEO_SIZES).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleInsert}
              disabled={!selectedVideo}
              className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                selectedVideo
                  ? "bg-[#F98B3D] text-white hover:bg-[#e07a2c]"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              Insert Video
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
